import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { GetJsDataInfo } from '@bingads-webui-clientcenter/js-data-cc-rest-base';
import { formatCoupon } from '@bingads-webui-campaign/recommendation-core';
import { Button } from '@fluentui/react-components';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { fetchCoupons, clearCouponsCache } from '@bingads-webui-campaign/overview-cards-common';
import CouponSetup from './conversion-setup/coupon-setup';
import { StringsMapping } from '../common/strings';

function fetchCouponsMethod({
  ccJsData,
  currencyCode,
  i18n,
  setDataState,
  instrumentationLog,
  blockCard,
  willFetchData,
  renderedWithData,
  unblockCard,
}) {
  blockCard();
  willFetchData();

  fetchCoupons({ ccJsData })
    .finally(() => {
      renderedWithData();
      unblockCard();
    }).then((coupons) => {
      if (coupons.length > 0) {
        const couponLocalized = formatCoupon({ i18n, coupon: coupons[0], currency: currencyCode });
        setDataState({ isDataLoaded: true, couponLocalized });
      } else {
        setDataState({ isDataLoaded: true, couponLocalized: [] });
      }
    })
    .catch((response) => {
      instrumentationLog.Error(response.message);
      setDataState({ isDataLoaded: true, couponLocalized: [] });
    });
}

function useFetchCoupon(props) {
  const {
    pageContext,
    i18n,
    instrumentationLog,
    blockCard,
    willFetchData,
    renderedWithData,
    unblockCard,
  } = props;

  const ccJsData = useMemo(() => GetJsDataInfo({
    cidFetcher: () => pageContext.CurrentCustomer.Id,
    uidFetcher: () => pageContext.CurrentUser.Id,
    willSupportCors: true,
  }, pageContext.CCBaseUrl, ['FeatureAdoptionCoupon']).jsDataCcRest, [pageContext.CCBaseUrl, pageContext.CurrentCustomer.Id, pageContext.CurrentUser.Id]);

  const { Id: accountId, CurrencyCode: currencyCode } = pageContext.CurrentAccount;
  const [{ isDataLoaded, couponLocalized }, setDataState] = useState({ isDataLoaded: false, couponLocalized: null });

  const fetchDate = useCallback(
    () => {
      fetchCouponsMethod({
        ccJsData,
        accountId,
        currencyCode,
        i18n,
        instrumentationLog,
        setDataState,
        blockCard,
        willFetchData,
        renderedWithData,
        unblockCard,
      });
    },
    [ccJsData, accountId, currencyCode, i18n, instrumentationLog, blockCard, willFetchData, renderedWithData, unblockCard]
  );

  useEffect(() => {
    fetchDate();
    return clearCouponsCache;
  }, [fetchDate]);

  return { isDataLoaded, couponLocalized };
}

function EmptyState(props) {
  const {
    router,
    pageContext,
    instrumentationLog,
    i18n,
    newI18n,
    permissions,
    blockCard,
    willFetchData,
    renderedWithData,
    unblockCard,
  } = props;

  const imgClass = 'conversion_tracking_setup_img';
  const requiredPermission = permissions.dynamic.OverviewPageFluentV2 || permissions.dynamic.OverviewPageFluent2;

  const titleString = useMemo(() =>
    newI18n.getString(StringsMapping.EmptyState_Title), [newI18n]);
  const subtitleString = useMemo(() =>
    newI18n.getString(StringsMapping.EmptyState_Subtitle), [newI18n]);
  const actionString = useMemo(() =>
    newI18n.getString(StringsMapping.EmptyState_Action), [newI18n]);

  const { isDataLoaded, couponLocalized } = useFetchCoupon({
    pageContext,
    i18n,
    instrumentationLog,

    blockCard,
    willFetchData,
    renderedWithData,
    unblockCard,
  });

  const hasCoupons = useMemo(() => _.size(couponLocalized) > 0, [couponLocalized]);

  const navigateToConversionGoals = useCallback(() => {
    if (router) {
      instrumentationLog.SetUpConversionClicked(hasCoupons);
      router.navigateToRoute('conversiongoals');
    }
  }, [hasCoupons, instrumentationLog, router]);

  const renderView = useCallback(() => {
    if (isDataLoaded) {
      if (hasCoupons) {
        instrumentationLog.CouponIsShown();
        return (<CouponSetup coupon={couponLocalized} newI18n={newI18n} />);
      }
      instrumentationLog.NoCoupon();
    }
    return (
      <React.Fragment>
        <div className="conversion-image-container">
          <div className={`conversion-image ${imgClass}`} />
        </div>
        <div className="title-text">{titleString}</div>
        <div className="subtitle-text">{subtitleString}</div>
      </React.Fragment>
    );
  }, [couponLocalized, hasCoupons, instrumentationLog, isDataLoaded, newI18n, subtitleString, titleString]);

  return (
    <div className="conversion-empty-state">
      {renderView()}
      <div className="entity-links">
        {requiredPermission ? (
          <Button onClick={navigateToConversionGoals}>{actionString}</Button>
          ) : (
            <DefaultButton onClick={navigateToConversionGoals} text={actionString} />
          )}
      </div>
    </div>
  );
}

EmptyState.defaultProps = {
  router: {
    navigateToRoute: _.noop,
  },
  instrumentationLog: {
    SetUpConversionClicked: _.noop,
    CouponIsShown: _.noop,
    NoCoupon: _.noop,
    Error: _.noop,
  },
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      OverviewPageFluentV2: false,
    }),
  }),
  blockCard: _.noop,
  willFetchData: _.noop,
  renderedWithData: _.noop,
  unblockCard: _.noop,
};

EmptyState.propTypes = {
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  router: PropTypes.shape({
    navigateToRoute: PropTypes.func,
  }),
  instrumentationLog: PropTypes.shape({
    SetUpConversionClicked: PropTypes.func,
    CouponIsShown: PropTypes.func,
    NoCoupon: PropTypes.func,
    Error: PropTypes.func,
  }),
  pageContext: PropTypes.shape({
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.number.isRequired,
    }).isRequired,
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      CurrencyCode: PropTypes.string.isRequired,
    }).isRequired,
    CurrentAccount: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      CurrencyCode: PropTypes.string.isRequired,
    }).isRequired,
    CCBaseUrl: PropTypes.string.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      OverviewPageFluentV2: PropTypes.bool,
      OverviewPageFluent2: PropTypes.bool,
    }),
  }),
  blockCard: PropTypes.func,
  willFetchData: PropTypes.func,
  renderedWithData: PropTypes.func,
  unblockCard: PropTypes.func,
};

export default EmptyState;
